import { NavigateBackLink } from '@exam-management/components';
import { type ItemSetCreation } from '@exam-management/graphql';
import {
  BaseItemSetSubQuestionSection,
  DetailSectionWrapper,
  EmptyItemSetQuestionOverlay,
  ItemSetCreationInformationSection,
  LocationFormFields,
  LocationSectionWrapper,
  QuestionLayout,
} from '@exam-management/qcontent-management';
import { ItemSetQuestionCreationForm } from '@exam-management/question-creation-task-creation';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import {
  useGetQBSLevelAndLevelItemsForTask,
  useQuestionCreationItemSetSearchParam,
  ItemSetCreationParagraphSection,
} from '@exam-management/question-creation-task-question-management';
import { Navigate, useParams } from '@exam-management/router';
import {
  AddRoundedIcon,
  Box,
  Button,
  Flex,
  FormButton,
  FormTextField,
  Loading,
  PageHeader,
  PaperSection,
  Text,
} from '@shared/components';

function getItemSetCreatedCount(itemSetCreation: ItemSetCreation[], size: number) {
  return itemSetCreation.filter((creation) => creation.size === size).length;
}

export default function QuestionCreationCreateItemSetPage() {
  const { taskId } = useParams('/task-management/question-creation/:taskId');
  const { task, roles } = useQuestionCreationTaskDetails();
  const [{ size }] = useQuestionCreationItemSetSearchParam();

  if (!size) {
    return (
      <Navigate
        to='/task-management/question-creation/:taskId/question-list'
        params={{ taskId }}
        replace
      />
    );
  }

  const currentItemSetCreatedCount = getItemSetCreatedCount(
    task.metadata?.requirement.itemSetCreation ?? [],
    size,
  );
  const itemSetExpectedCreatedCount =
    task.metadata?.requirement.itemSetCondition?.find((condition) => condition.size === size)
      ?.needTotal ?? 0;

  if (currentItemSetCreatedCount >= itemSetExpectedCreatedCount) {
    return (
      <Navigate to='/my-task/question-creation/:taskId/question-list' params={{ taskId }} replace />
    );
  }

  const qBankStructureId = task.metadata?.requirement.qBankStructure?.id ?? '';

  const {
    qbsLevelItems,
    qbsLevels,
    loading: isGettingQBSLevel,
  } = useGetQBSLevelAndLevelItemsForTask(qBankStructureId, task.id);
  if (isGettingQBSLevel) return <Loading sx={{ height: '100%' }} />;

  return (
    <>
      <PageHeader
        title='Add New Item Set'
        subtitleSection={
          <Flex
            sx={{
              gap: (theme) => theme.spacing(4),
              alignItems: 'center',
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            <Text variant='body2'>Task ID: {task.code}</Text>
            <Text variant='body2'>{task.metadata?.requirement.qBankStructure?.name}</Text>
          </Flex>
        }
        linkAdornment={
          <NavigateBackLink
            to='/task-management/question-creation/:taskId/question-list'
            params={{ taskId }}
          />
        }
      />
      <ItemSetQuestionCreationForm qbsLevels={qbsLevels}>
        <QuestionLayout
          sideContent={
            <PaperSection title='Location'>
              <LocationSectionWrapper>
                <LocationFormFields
                  prefix='location'
                  qbsLevels={qbsLevels}
                  qbsLevelItems={qbsLevelItems}
                />
              </LocationSectionWrapper>
            </PaperSection>
          }
        >
          <Flex sx={{ width: '100%', flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
            <PaperSection title='Question Details'>
              <DetailSectionWrapper>
                {roles.isAssignee ? (
                  <ItemSetCreationInformationSection />
                ) : (
                  <FormTextField
                    label='Item Set Name'
                    placeholder='กรอกชื่อ Item Set'
                    name='details.name'
                  />
                )}
                <ItemSetCreationParagraphSection taskId={taskId} />
                <BaseItemSetSubQuestionSection
                  slotProps={{
                    title: {
                      endAdornment: (
                        <Flex sx={{ gap: (theme) => theme.spacing(6), alignItems: 'center' }}>
                          <Text variant='body2'>0/{size} Sub Questions</Text>
                          <Button variant='outlined' disabled startIcon={<AddRoundedIcon />}>
                            Add Sub Question
                          </Button>
                        </Flex>
                      ),
                    },
                  }}
                >
                  <EmptyItemSetQuestionOverlay />
                </BaseItemSetSubQuestionSection>
              </DetailSectionWrapper>
            </PaperSection>
            <Box>
              <FormButton label='Create Item Set' />
            </Box>
          </Flex>
        </QuestionLayout>
      </ItemSetQuestionCreationForm>
    </>
  );
}
